import React from 'react';
import styled, {css} from 'styled-components';

import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Style.
 */

export const carouselAnimationCurve = 'cubic-bezier(0.25, 1, 0.25, 1)';

/**
 * Lets us share the grid styling between the Carousel wrapper and CarouselSlideContent.
 */
export const addSlideGridContainerStyle = () => css`
  display: grid;
  grid-column-gap: 32px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) 90px;
  grid-template-areas:
    'left-content right-content'
    'mid-left mid-right'
    'left-footer right-footer';

  min-height: 0;
  min-width: 0;
`;

/**
 * Spans the entire slide, including footers, so we can display background
 * images that span the entire right/left column.
 */
export const CarouselSlideContent = styled.div`
  grid-row: 1 / span right-footer-end;
  grid-column: 1 / span right-footer-end;

  ${addSlideGridContainerStyle()}

  @keyframes slideIn {
    0% {
      transform: translateY(15px);
    }

    100% {
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  opacity: 0;
  animation:
    slideIn 800ms ${carouselAnimationCurve} forwards,
    fadeIn 300ms linear forwards;
  animation-delay: 100ms;
`;

export const CarouselSlideContentLeft = styled.div`
  padding-left: 50px;
  padding-top: 50px;
  grid-area: left-content;
`;

export const CarouselSlideTitle = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: 50px;
  font-weight: ${fontWeights.bold};
  margin-bottom: 16px;
`;

export const CarouselSlideDescription = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.light};
  font-size: 24px;
  line-height: 1.33;
`;

export const CarouselSlideFooterLeft = styled.div`
  display: grid;
  grid-area: left-footer;
  justify-content: left;
  align-content: flex-end;
  padding-left: 40px;
  padding-bottom: 20px;
`;

export const CarouselSlideContentRight = styled.div`
  padding-right: 50px;
  padding-top: 50px;
  grid-area: right-content;
`;

export const CarouselSlideFooterRight = styled.div`
  grid-area: right-footer;
  display: grid;
  justify-content: right;
  align-content: flex-end;
  padding-right: 20px;
  padding-bottom: 20px;
`;

export const CarouselSlideColumnRight = styled.div`
  grid-column-start: right-content-start;
  grid-column-end: right-content-end;
  grid-row-start: right-content-start;
  grid-row-end: right-footer-end;

  position: relative;
`;

export const CarouselSlideButtonsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 10px;
  place-items: center;
`;

/*
 * Slides.
 */

export interface CarouselSlideDefinition {
  renderContent: (props: CarouselSlideContentProps) => React.ReactElement;
  // Contains any images the slide uses so the carousel can try to preload them.
  imageUrls?: ReadonlyArray<string>;
  hasComposeChannel?: boolean;
}

/** Every FULLSCREEN slide's renderContent will be passed these props. */
export interface CarouselSlideContentProps {
  index: number;
  onNextClick: (isSkip?: boolean) => void;
  onPreviousClick: (() => void) | undefined;
  hasComposeChannel?: boolean;
  totalStepCount?: number;
}

/*
 * Buttons.
 */

export enum CarouselNavigationButtonVariantsEnum {
  PRIMARY = 'PRIMARY',
  LINK = 'LINK',
  // Greyed out link.
  SUBDUED_LINK = 'SUBDUED_LINK',
}
